// Procedural Budapest: layout, buildings, landmarks, props, collision + path graphs.
import * as THREE from 'three';
import type { AABB, CityData, CollisionResult, PathGraph, PointOfInterest } from './types';
import { makeRng } from './util';
import { makeTextures } from './city_textures';
import { Bucket, QuadSoup, type BuildCtx, type NightItem } from './city_common';
import { Layout, buildRoadGraph, buildWalkGraph, ColliderHash, RoadRaster, HALF, SIDEWALK } from './city_layout';
import { buildGround } from './city_ground';
import { buildBlocks } from './city_buildings';
import { buildLandmarks } from './city_landmarks';
import { buildProps } from './city_props';

/** Extra public members on the city object (not part of the CityData contract). */
export interface CityExtras {
  /** world xz of every lamp bulb (for the environment's point lights) */
  lampPositions: Float32Array;
  /** water shader uniforms the environment may drive (sky colour, sun) */
  waterUniforms: { [k: string]: THREE.IUniform };
  layout: Layout;
  /** number of scene objects the city added (draw-call estimate) */
  objectCount: number;
}

class City implements CityData, CityExtras {
  bounds: AABB;
  roadSegments: CityData['roadSegments'];
  waterRects: AABB[];
  buildingRects: AABB[];
  colliders: AABB[];
  roadGraph: PathGraph;
  walkGraph: PathGraph;
  parkingSpots: { pos: THREE.Vector3; heading: number }[];
  pois: PointOfInterest[];
  raycastTargets: THREE.Object3D[];
  lampPositions: Float32Array;
  waterUniforms: { [k: string]: THREE.IUniform };
  layout: Layout;
  objectCount = 0;

  private hash: ColliderHash;
  private raster: RoadRaster;
  private night = -1;
  private nightItems: NightItem[];
  private updaters: ((dt: number, t: number) => void)[];
  private t = 0;
  private poiMap = new Map<string, PointOfInterest>();

  constructor(a: {
    bounds: AABB;
    roadSegments: CityData['roadSegments'];
    waterRects: AABB[];
    buildingRects: AABB[];
    colliders: AABB[];
    roadGraph: PathGraph;
    walkGraph: PathGraph;
    parkingSpots: { pos: THREE.Vector3; heading: number }[];
    pois: PointOfInterest[];
    raycastTargets: THREE.Object3D[];
    lampPositions: Float32Array;
    waterUniforms: { [k: string]: THREE.IUniform };
    layout: Layout;
    hash: ColliderHash;
    raster: RoadRaster;
    nightItems: NightItem[];
    updaters: ((dt: number, t: number) => void)[];
  }) {
    this.bounds = a.bounds;
    this.roadSegments = a.roadSegments;
    this.waterRects = a.waterRects;
    this.buildingRects = a.buildingRects;
    this.colliders = a.colliders;
    this.roadGraph = a.roadGraph;
    this.walkGraph = a.walkGraph;
    this.parkingSpots = a.parkingSpots;
    this.pois = a.pois;
    this.raycastTargets = a.raycastTargets;
    this.lampPositions = a.lampPositions;
    this.waterUniforms = a.waterUniforms;
    this.layout = a.layout;
    this.hash = a.hash;
    this.raster = a.raster;
    this.nightItems = a.nightItems;
    this.updaters = a.updaters;
    for (const p of this.pois) this.poiMap.set(p.key, p);
  }

  poi(key: string): PointOfInterest {
    return this.poiMap.get(key) ?? this.pois[0];
  }
  resolveCircle(x: number, z: number, r: number): CollisionResult | null {
    return this.hash.resolveCircle(x, z, r);
  }
  isRoad(x: number, z: number): boolean {
    return this.raster.get(x, z);
  }
  isWater(x: number, z: number): boolean {
    const L = this.layout;
    if (x <= L.waterX0 || x >= L.waterX1) return false;
    for (const j of L.bridgeRows) if (Math.abs(z - L.roadZ[j]) < HALF + SIDEWALK + 0.5) return false;
    return true;
  }
  clampToBounds(v: THREE.Vector3): THREE.Vector3 {
    const b = this.bounds;
    if (v.x < b.minX + 1) v.x = b.minX + 1;
    else if (v.x > b.maxX - 1) v.x = b.maxX - 1;
    if (v.z < b.minZ + 1) v.z = b.minZ + 1;
    else if (v.z > b.maxZ - 1) v.z = b.maxZ - 1;
    return v;
  }
  setNightFactor(f: number): void {
    f = f < 0 ? 0 : f > 1 ? 1 : f;
    if (Math.abs(f - this.night) < 0.004) return;
    this.night = f;
    for (let i = 0; i < this.nightItems.length; i++) this.nightItems[i](f);
  }
  update(dt: number): void {
    this.t += dt;
    for (let i = 0; i < this.updaters.length; i++) this.updaters[i](dt, this.t);
  }
}

export function buildCity(scene: THREE.Scene, seed: number): CityData {
  const t0 = performance.now();
  const rng = makeRng(seed);
  const tex = makeTextures(seed);
  const layout = new Layout();
  const lit = new Bucket('lit');
  const ctx: BuildCtx = {
    rng,
    scene,
    tex,
    layout,
    solid: new Bucket('solid'),
    metal: new Bucket('metal'),
    glass: new Bucket('glass'),
    stoneLit: new Bucket('stoneLit'),
    lit,
    redLit: lit,
    blueLit: lit,
    facades: new Map(),
    roofs: new QuadSoup('roofs'),
    colliders: [],
    buildingRects: [],
    raycast: [],
    pois: [],
    parking: [],
    nightItems: [],
    updaters: [],
    lamps: [],
    neonRequests: [],
    signRequests: [],
    billboardRequests: [],
    treeRequests: [],
    benchRequests: [],
    lampRequests: [],
    bulbRequests: [],
    wires: [],
    containerRequests: [],
    parkSoup: new QuadSoup('parks'),
    marketSoup: new QuadSoup('marketRoof'),
  };

  const root = new THREE.Group();
  root.name = 'city';

  // ---- geometry passes ------------------------------------------------------------
  const ground = buildGround(ctx);
  for (const m of ground.meshes) root.add(m);
  buildBlocks(ctx, ctx.parkSoup);
  buildLandmarks(ctx);
  const props = buildProps(ctx);
  for (const m of props.meshes) root.add(m);

  // ---- materials + merged meshes ---------------------------------------------------
  const addMerged = (bucket: Bucket, material: THREE.Material, castShadow = true, receiveShadow = true) => {
    const g = bucket.build();
    if (!g) return null;
    const m = new THREE.Mesh(g, material);
    m.name = bucket.name;
    m.castShadow = castShadow;
    m.receiveShadow = receiveShadow;
    root.add(m);
    ctx.raycast.push(m);
    return m;
  };
  const addSoup = (soup: QuadSoup, material: THREE.Material, castShadow = true, receiveShadow = true) => {
    const g = soup.build();
    if (!g) return null;
    const m = new THREE.Mesh(g, material);
    m.name = soup.name;
    m.castShadow = castShadow;
    m.receiveShadow = receiveShadow;
    root.add(m);
    ctx.raycast.push(m);
    return m;
  };

  addMerged(ctx.solid, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.86, metalness: 0.02 }));
  addMerged(ctx.metal, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.45, metalness: 0.35 }));
  addMerged(ctx.glass, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.12, metalness: 0.15, transparent: true, opacity: 0.62 }), false, false);
  const stoneLitMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, metalness: 0.02, emissive: 0xffc98a, emissiveIntensity: 0 });
  addMerged(ctx.stoneLit, stoneLitMat);
  ctx.nightItems.push((f) => (stoneLitMat.emissiveIntensity = f * 0.42));
  const litMat = new THREE.MeshBasicMaterial({ vertexColors: true, color: 0xffffff, fog: true });
  addMerged(lit, litMat, false, false);
  ctx.nightItems.push((f) => litMat.color.setScalar(0.9 + f * 1.6));

  const facadeMats: THREE.MeshStandardMaterial[] = [];
  for (const [key, soup] of ctx.facades) {
    const st = key === 'shop' ? tex.shop : tex.facades[key];
    const modern = key === 'modern';
    const mat = new THREE.MeshStandardMaterial({
      map: st.map,
      emissiveMap: st.emissive,
      emissive: key === 'shop' ? 0xffffff : 0xffe2b8,
      emissiveIntensity: 0,
      vertexColors: true,
      roughness: modern ? 0.35 : 0.92,
      metalness: modern ? 0.1 : 0,
    });
    facadeMats.push(mat);
    addSoup(soup, mat);
  }
  ctx.nightItems.push((f) => {
    const k = f * 1.9;
    for (const m of facadeMats) m.emissiveIntensity = k;
  });
  addSoup(ctx.roofs, new THREE.MeshStandardMaterial({ map: tex.roof, vertexColors: true, roughness: 1, metalness: 0 }), false, true);
  addSoup(ctx.parkSoup, new THREE.MeshStandardMaterial({ map: tex.grass, vertexColors: true, roughness: 1, metalness: 0 }), false, true);
  addSoup(ctx.marketSoup, new THREE.MeshStandardMaterial({ map: tex.marketRoof, vertexColors: true, roughness: 0.55, metalness: 0.05 }), true, true);
  ctx.nightItems.push((f) => {
    ground.waterUniforms.uNight.value = f;
  });

  scene.add(root);

  // ---- data ---------------------------------------------------------------------------
  const hash = new ColliderHash(layout.bounds);
  for (const c of ctx.colliders) hash.insert(c);
  const roadGraph = buildRoadGraph(layout);
  const walkGraph = buildWalkGraph(layout);
  const lampPositions = new Float32Array(ctx.lamps.length * 2);
  ctx.lamps.forEach((l, i) => {
    lampPositions[i * 2] = l.x;
    lampPositions[i * 2 + 1] = l.z;
  });
  let objectCount = 0;
  root.traverse((o) => {
    if ((o as THREE.Mesh).isMesh || (o as THREE.Points).isPoints || (o as THREE.LineSegments).isLineSegments) objectCount++;
  });

  const city = new City({
    bounds: layout.bounds,
    roadSegments: ground.roadSegments,
    waterRects: ground.waterRects,
    buildingRects: ctx.buildingRects,
    colliders: ctx.colliders,
    roadGraph,
    walkGraph,
    parkingSpots: ctx.parking,
    pois: ctx.pois,
    raycastTargets: ctx.raycast,
    lampPositions,
    waterUniforms: ground.waterUniforms,
    layout,
    hash,
    raster: ground.raster,
    nightItems: ctx.nightItems,
    updaters: ctx.updaters,
  });
  city.objectCount = objectCount;
  city.setNightFactor(0);
  console.log(
    `[city] built in ${(performance.now() - t0).toFixed(0)} ms: ${objectCount} objects, ${ctx.colliders.length} colliders, ${roadGraph.lanes.length} road lanes, ${walkGraph.lanes.length} walk lanes, ${ctx.parking.length} parking spots, ${ctx.pois.length} pois`,
  );
  return city;
}
